import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  assignCustomQuiz,
  createCustomQuiz,
  deleteCustomQuiz,
  getAssignList,
  getAssignListForStudent,
  getCustomQuizById,
  getCustomQuizList,
  getCustomQuizListForDropDown,
  updateCustomQuiz,
} from './api';
import { IQuestion } from '../../QuestionSlice';
import { ISection } from '../SectionSlice';

export interface ICustomQuiz {
  _id?: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
  questions?: IQuestion[];
  isPublished?: boolean;
}

interface ICustomQuizState {
  status: 'idle' | 'loading' | 'rejected';
  customQuiz?: ICustomQuiz;
  customQuizListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICustomQuiz>;
  };
  assignListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ISection>;
  };
  customQuizListForDropDown: Array<ICustomQuiz>;
  assignListDataForStudent?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ISection>;
  };
  quizNameModalOpen: boolean;
}

const initialState: ICustomQuizState = {
  status: 'idle',
  customQuiz: undefined,
  customQuizListData: undefined,
  assignListData: undefined,
  customQuizListForDropDown: [],
  assignListDataForStudent: undefined,
  quizNameModalOpen: false,
};

export const CustomQuizSlice = createSlice({
  name: 'customQuiz',
  initialState,
  reducers: {
    resetCustomQuiz: (state) => {
      state.customQuiz = undefined;
    },
    resetAssignList: (state) => {
      state.assignListData = undefined;
    },
    setQuizNameModalOpen: (state, action) => {
      state.quizNameModalOpen = action.payload;
    },
    updateCustomQuizTitle: (state, action) => {
      if (state.customQuiz) {
        state.customQuiz.title = action.payload;
      } else {
        state.customQuiz = {
          title: action.payload,
        };
      }
    },
    addQuestionToCustomQuiz: (state, action) => {
      if (action.payload) {
        if (state.customQuiz) {
          const questionExists = state.customQuiz.questions?.some(
            (q) => q._id === action.payload._id
          );
          if (!questionExists) {
            state.customQuiz.questions = [
              ...(state.customQuiz.questions || []),
              action.payload,
            ];
          }
        } else {
          state.customQuiz = {
            questions: [action.payload],
          };
        }
      }
    },
    removeQuestionFromCustomQuiz: (state, action) => {
      if (state.customQuiz && action.payload) {
        state.customQuiz.questions =
          state.customQuiz.questions?.filter((q) => q._id !== action.payload) ||
          [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCustomQuiz.fulfilled, (state, action) => {
        state.status = 'idle';
        state.customQuiz = action.payload;
      })
      .addCase(getCustomQuizById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.customQuiz = action.payload;
      })
      .addCase(getCustomQuizList.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;
        const newCustomQuizList = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };
        state.customQuizListData = newCustomQuizList;
      })
      .addCase(getAssignList.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;
        const newAssignList = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };
        state.assignListData = newAssignList;
      })
      .addCase(assignCustomQuiz.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && state.assignListData) {
          const newAssignList = {
            ...state.assignListData,
          };

          newAssignList.list.unshift(result);

          if (newAssignList.totalDocs) {
            newAssignList.totalDocs += 1;
          } else {
            newAssignList.totalDocs = 1;
          }

          state.assignListData = newAssignList;
        }
      })
      .addCase(updateCustomQuiz.fulfilled, (state, action) => {
        state.status = 'idle';
        state.customQuiz = action.payload;
      })
      .addCase(getCustomQuizListForDropDown.fulfilled, (state, action) => {
        state.status = 'idle';
        state.customQuizListForDropDown = action.payload;
      })
      .addCase(getAssignListForStudent.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;
        const newAssignList = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };
        state.assignListDataForStudent = newAssignList;
      })
      .addCase(deleteCustomQuiz.fulfilled, (state, action) => {
        state.status = 'idle';
        state.customQuizListData = {
          ...state.customQuizListData,
          list:
            state.customQuizListData?.list.filter(
              (item) => item._id !== action.payload._id
            ) || [],
        };
      });

    // Matchers
    builder
      .addMatcher(
        isPending(
          createCustomQuiz,
          getCustomQuizById,
          getCustomQuizList,
          getAssignList,
          getAssignListForStudent,
          updateCustomQuiz,
          deleteCustomQuiz
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          createCustomQuiz,
          getCustomQuizById,
          getCustomQuizList,
          getAssignList,
          getAssignListForStudent,
          updateCustomQuiz,
          deleteCustomQuiz
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  resetCustomQuiz,
  resetAssignList,
  updateCustomQuizTitle,
  addQuestionToCustomQuiz,
  removeQuestionFromCustomQuiz,
  setQuizNameModalOpen,
} = CustomQuizSlice.actions;

export const getCustomQuizState = (state: RootState): RootState['customQuiz'] =>
  state.customQuiz;

export * from './api';
export default CustomQuizSlice.reducer;
